import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";

const UserContext = createContext({
  userProfile: undefined,
  token: undefined,
  callApi: () => 0,
  login: () => 0,
  logout: () => 0,
});

const PREVIEW_KEY = "lux-preview-active";
const PREVIEW_GROUP = "editorial-staff";
const AUTH_COOKIE = window.AUTH_COOKIE || "X_AUTH_LUXOTTICATOKEN";
const LOGGED_COOKIE = "X_LUXUSER_LOGGED";
//const LEONARDO_COOKIE = "UserToken";

const getPreviewUrl = (path) => {
  const current = new URL(`/bff${path}`, window.location.origin);
  const params = new URLSearchParams(current.search);
  params.set("preview", true);
  return `${current.pathname}?${params.toString()}`;
};

export const UserProvider = ({ children }) => {
  const [isLogged, setIsLogged] = useState(!!Cookies.get(LOGGED_COOKIE));
  const [userProfile, setUserProfile] = useState();
  const loadedProfile = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const previewParam = searchParams.get("preview");
  const [previewRequested, setPreviewRequested] = useState(
    () =>
      previewParam === "true" ||
      (previewParam !== "false" && sessionStorage.getItem(PREVIEW_KEY) === "true")
  );
  const previewActive = useMemo(() => {
    if (previewRequested) {
      return userProfile
        ? previewRequested && userProfile.groups?.includes(PREVIEW_GROUP)
        : undefined;
    }
    return false;
  }, [userProfile, previewRequested]);

  useEffect(() => {
    sessionStorage.setItem(PREVIEW_KEY, previewRequested);
  }, [previewRequested]);

  const clearUserData = useCallback(() => {
    loadedProfile.current = false;
    //Cookies.remove(LEONARDO_COOKIE, { path: "", domain: `.${window.location.host}` });
    setIsLogged();
    setUserProfile();
  }, []);

  const showPreview = useCallback(
    (active) => {
      if (active) {
        setPreviewRequested(true);
      } else {
        const params = new URLSearchParams(searchParams);
        params.delete("preview");
        setSearchParams(params);
        setPreviewRequested(false);
      }
    },
    [searchParams, setSearchParams]
  );

  const callApi = useCallback(
    async (path, options = {}) => {
      const { headers, mock = false, skipPreview = false, ...otherOptions } = options;
      if (!isLogged) {
        console.warn("User not logged");
        clearUserData();
        return;
      }

      const response = await fetch(
        mock ? `/mock${path}` : previewActive && !skipPreview ? getPreviewUrl(path) : `/bff${path}`,
        {
          headers: {
            X_CSFR: "IBFsrw65WAoevFitnuY8VeyMajKgaPpg",
            Accept: "*/*",
            ...headers,
          },
          ...otherOptions,
        }
      );
      if (!response.ok) {
        if (response.status === 401) {
          clearUserData();
        }
        const error = new Error(`Error calling ${path} ${response.status} ${response.statusText}`);
        error.status = response.status;
        const resHeaders = Object.fromEntries(response.headers.entries());
        if (
          Number(resHeaders["content-length"]) > 0 &&
          resHeaders["content-type"].toLowerCase() === "application/json"
        ) {
          try {
            const data = await response.json();
            error.data = data;
            console.error("Error content: ", data);
          } catch (e) {
            console.warn("Could not parse error response: ", e);
          }
        }
        throw error;
      }
      const resHeaders = Object.fromEntries(response.headers.entries());
      if (resHeaders["content-type"]?.startsWith("text/plain")) {
        const data = await response.text();
        return data;
      } else if (Number(resHeaders["content-length"]) > 0) {
        if (resHeaders["content-type"].toLowerCase() === "application/json") {
          const data = await response.json();
          return data;
        } else {
          const file = await response.blob();
          return file;
        }
      } else {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let text = "";
        while (true) {
          const { value, done } = await reader.read();
          const chunk = decoder.decode(value || new Uint8Array(), { stream: !done });
          text += chunk;
          if (done) break;
        }
        if (text) {
          return JSON.parse(text);
        }
      }
    },
    [isLogged, clearUserData, previewActive]
  );

  const loadProfile = useCallback(async () => {
    loadedProfile.current = true;
    try {
      const data = await callApi(`/profile`);
      setUserProfile(data);
      if (data.language) {
        localStorage.setItem("lang-interface", data.language);
      }
    } catch (err) {
      console.error("Error loading profile: ", err);
      setUserProfile(null);
      loadedProfile.current = false;
      // TODO feedback errore
    }
  }, [callApi]);

  useEffect(() => {
    if (isLogged && !loadedProfile.current) {
      loadProfile();
    }
  }, [isLogged, loadProfile]);

  const login = useCallback(async (username, password, mock = false) => {
    const response = await fetch(mock ? `/mock/authorization/login` : `/bff/authorization/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      body: JSON.stringify({
        username,
        password,
      }),
    });
    if (!response.ok) {
      throw new Error(`Login error ${response.status} ${response.statusText}`);
    }
    const { redirectUrl } = await response.json();
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }
    setIsLogged(true);
  }, []);

  const logout = useCallback(async () => {
    await callApi(`/authorization/logout`, {
      method: "POST",
    });
    clearUserData();
  }, [callApi, clearUserData]);

  const openAuthLink = useCallback(
    (e) => {
      e.preventDefault();
      const url = e.currentTarget.href;
      if (isLogged && !!Cookies.get(LOGGED_COOKIE)) {
        // TODO remove this case
        const authToken = Cookies.get(AUTH_COOKIE);
        if (url.split("/").includes("tamtamy") && authToken) {
          const form = document.createElement("form");
          form.target = "_blank";
          form.method = "POST";
          form.action = url;
          form.enctype = "multipart/form-data";

          const jwtInput = document.createElement("input");
          jwtInput.type = "hidden";
          jwtInput.name = "jwt";
          jwtInput.value = authToken;
          form.appendChild(jwtInput);

          document.body.appendChild(form);
          form.submit();
          document.body.removeChild(form);
        } else {
          window.open(url, "_blank");
        }
      } else {
        clearUserData();
      }
    },
    [isLogged, clearUserData]
  );

  const contextValue = useMemo(
    () => ({
      logged: !!isLogged,
      isPreview: previewActive,
      openAuthLink,
      showPreview,
      userProfile,
      login,
      logout,
      callApi,
      loadProfile,
    }),
    [
      isLogged,
      openAuthLink,
      previewActive,
      showPreview,
      userProfile,
      login,
      logout,
      callApi,
      loadProfile,
    ]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export default UserContext;
